import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { useDebouncedCallback } from 'use-debounce';



const FeedbackAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { feedbackType, feedbackTypeLabel, propertyId } = location.state || {};
  
  const [isSearchingMobile, setIsSearchingMobile] = useState(false);
  const [isSearchingEmail, setIsSearchingEmail] = useState(false);
  const [isSearchingName, setIsSearchingName] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactResultsEmail, setContactResultsEmail] = useState([]);
  const [contactResultsMobile, setContactResultsMobile] = useState([]);
  const [contactResultsFirstLastName, setContactResultsFirstLastName] = useState([]);
  const [enquirySource, setEnquirySource] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [Salesperson, setSalesperson] = useState([]);
  const [Openhomes, setOpenhomes] = useState([]);

  const interestLevels = [
    { label: 'Not interested', value: '0' },
    { label: 'Somewhat interested', value: '1' },
    { label: 'Interested', value: '2' },
    { label: 'Very Interested', value: '3' },
  ];

  const fetchData = useCallback(async () => {
    try {
      const spanofDays = 28;
      const [
        salespersonResponse,
        contactTypesResponse,
        enquirySourceResponse,
        openhomeResponse
      ] = await Promise.all([
        getSalesperson("contacts_view"),
        getContactTypes(),
        getEnquirySource(),
        getOpenhomes(spanofDays, propertyId)
      ]);
      setSalesperson(salespersonResponse.data);
      setContactTypes(contactTypesResponse.data);
      setEnquirySource(enquirySourceResponse.data);
      setOpenhomes(openhomeResponse.data);
    } catch (error) {
    }
  }, [propertyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchContacts = useDebouncedCallback(async (mobile, email, source) => {
    if (source === 'mobile' && mobile) {
      setIsSearchingMobile(true);
    } else if (source === 'email' && email) {
      setIsSearchingEmail(true);
    }
    
    try {
      const contacts = await getContactsByCommunicationDetail(mobile, email, source);
      if (contacts.data && contacts.data.length > 0) {
        if (source === 'email') {
          setContactResultsEmail(contacts.data);
        } else {
          setContactResultsMobile(contacts.data);
        }
      } else {
        setContactResultsMobile([]);
        setContactResultsEmail([]);
      }
    } catch (error) {
      setContactResultsMobile([]);
      setContactResultsEmail([]);
    }

    if (source === 'mobile' && mobile) {
      setIsSearchingMobile(false);
    } else if (source === 'email' && email) {
      setIsSearchingEmail(false);
    }
  }, 500);

  const searchContactsFirstLastName = useDebouncedCallback(async (firstname, lastname) => {
    setIsSearchingName(true);
    try {
      const contactsFNResults = await getContactsByFirstLastName(firstname, lastname);
      if (contactsFNResults.data && contactsFNResults.data.length > 0) {
        setContactResultsFirstLastName(contactsFNResults.data);
      } else {
        setContactResultsFirstLastName([]);
      }
    } catch (error) {
      setContactResultsFirstLastName([]);
    }
    setIsSearchingName(false);
  }, 500);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      let buyer = '';
      let feedbackDate = '';

      if (values.addDate) {
        feedbackDate = values.addDate;
      } else {
        feedbackDate = getCurrentDateInISOFormat(new Date());
      }

      if (feedbackType === 2 || feedbackType === 3) {
        if (values.isSearchContact) {
          buyer = values.searchContactId;
        } else {
          const resultsContact = await addContact(values);
          buyer = resultsContact.data?.contactID;
        }
        if (!buyer) {
          return;
        }
      }

      const addFeedbackJSONbody = {
        ...values,
        buyer,
      };

      const resultsAddFeedback = await addFeedback(addFeedbackJSONbody);

      if (resultsAddFeedback.status === 200 || resultsAddFeedback.status === 201) {
        localStorage.setItem('addFeedback', 'true');
        setIsSubmitting(false);
        navigate(-1);
      }
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const ContactCard = ({ contact, onSelect }) => (
    <div className="contact-card" onClick={() => onSelect(contact)}>
      <div className="contact-header">
        <div className="contact-name">
          <span className="icon">👤</span>
          {contact.firstname} {contact.lastname}
        </div>
        <div className={`contact-status status-${contact.contactstatusid}`}>
          {contact.contactstatusid === 1 ? 'Current' :
           contact.contactstatusid === 2 ? 'Archived' : 'Deleted'}
        </div>
      </div>
      {contact.mobile && (
        <div className="contact-info">
          <span className="icon">📱</span>
          {contact.mobile}
        </div>
      )}
      {contact.email && (
        <div className="contact-info">
          <span className="icon">📧</span>
          {contact.email}
          {contact.emailVerified === 1 && <span className="verified">✓</span>}
        </div>
      )}
      {contact.agentfirstname && contact.agentlastname && (
        <div className="contact-info">
          <span className="icon">💼</span>
          {contact.agentfirstname} {contact.agentlastname}
        </div>
      )}
    </div>
  );

  const LoadingSpinner = () => (
    <div className="loading-container">
      <div className="spinner"></div>
      <p>Searching contacts…</p>
    </div>
  );

  const NoResultsCard = () => (
    <div className="no-results-card">
      <div className="no-results-icon">🔍</div>
      <p>No contacts found</p>
    </div>
  );

  return (
    <Formik
      initialValues={{
        foreignKey: propertyId,
        searchContactId: '',
        interestLevel: 0,
        contractRequested: 0,
        priceFeedbackFrom: '',
        priceFeedbackTo: '',
        agentNotes: '',
        agentNotesToClient: '',
        addToContactHistory: 0,
        interested: 0,
        isAddContact: 0,
        addContact: 0,
        propertyOpenTimeId: '',
        buyerName: '',
        feedbackTypeId: feedbackType,
        addDate: '',
        searchFirstName: '',
        searchLastName: '',
        searchMobile: '',
        searchEmail: '',
        firstNameContact: '',
        lastNameContact: '',
        contactTypeID: '',
        contactEmail: '',
        contactNumber: '',
        enableSMSBroadcast: 0,
        enabledForBroadCast: 0,
        contactSourceId: '',
        useDatePicker: 0,
        isSearchContact: 0,
        feedback: ''
      }}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => (
        <div className="feedback-form-container">
          <div className="form-content">
            {(feedbackType === 2 || feedbackType === 3) && (
              <>
                <h2 className="section-title">Buyer Information</h2>
                
                <div className="buyer-container">
                  <div className="switch-row">
                    <input
                      type="checkbox"
                      id="isSearchContact"
                      checked={values.isSearchContact === 1}
                      onChange={() => {
                        const newValue = values.isSearchContact === 1 ? 0 : 1;
                        setFieldValue('isSearchContact', newValue);
                        if (newValue === 1) {
                          setFieldValue('isAddContact', 0);
                          setFieldValue('firstNameContact', '');
                          setFieldValue('lastNameContact', '');
                          setFieldValue('contactTypeID', '');
                          setFieldValue('salespersonid', '');
                          setFieldValue('contactEmail', '');
                          setFieldValue('contactNumber', '');
                          setFieldValue('enableSMSBroadcast', '');
                          setFieldValue('enabledForBroadCast', '');
                        }
                      }}
                    />
                    <label htmlFor="isSearchContact">Search An Existing Contact</label>
                  </div>

                  {!selectedContact ? (
                    <>
                      {values.isSearchContact ? (
                        <>
                          <div className="form-section">
                            <h3 className="section-title">Search First and Last Name</h3>
                            <div className="input-group">
                              <input
                                type="text"
                                placeholder="First Name"
                                value={values.searchFirstName}
                                onChange={(e) => {
                                  setFieldValue('searchFirstName', e.target.value);
                                  searchContactsFirstLastName(e.target.value, values.searchLastName, 'name');
                                }}
                                onFocus={() => {
                                  setContactResultsEmail([]);
                                  setContactResultsMobile([]);
                                  setFieldValue('searchEmail', '');
                                  setFieldValue('searchMobile', '');
                                }}
                                className="form-input"
                              />
                              <input
                                type="text"
                                placeholder="Last Name"
                                value={values.searchLastName}
                                onChange={(e) => {
                                  setFieldValue('searchLastName', e.target.value);
                                  searchContactsFirstLastName(values.searchFirstName, e.target.value, 'name');
                                }}
                                onFocus={() => {
                                  setContactResultsEmail([]);
                                  setContactResultsMobile([]);
                                  setFieldValue('searchEmail', '');
                                  setFieldValue('searchMobile', '');
                                }}
                                className="form-input"
                              />
                            </div>

                            {values.searchFirstName?.length > 1 && values.searchLastName?.length > 1 && (
                              <>
                                {isSearchingName && <LoadingSpinner />}
                                {!isSearchingName && contactResultsFirstLastName?.length > 0 && (
                                  <div className="contact-results">
                                    {contactResultsFirstLastName.map((item) => (
                                      <ContactCard
                                        key={`${item.contactid}-${item.firstname}`}
                                        contact={item}
                                        onSelect={() => {
                                          setFieldValue('searchContactId', item.contactid);
                                          setFieldValue('addContact', true);
                                          setSelectedContact(item);
                                          setContactResultsFirstLastName([]);
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                                {!isSearchingName && contactResultsFirstLastName?.length === 0 && (
                                  <NoResultsCard />
                                )}
                              </>
                            )}

                            <h3 className="section-title">Search Mobile or Email</h3>
                            <input
                              type="tel"
                              placeholder="Mobile"
                              value={values.searchMobile?.trim() || ''}
                              onChange={(e) => {
                                setFieldValue('searchMobile', e.target.value);
                                searchContacts(e.target.value, e.target.value, 'mobile');
                              }}
                              onFocus={() => {
                                setContactResultsEmail([]);
                                setContactResultsFirstLastName([]);
                                setFieldValue('searchEmail', '');
                                setFieldValue('searchFirstName', '');
                                setFieldValue('searchLastName', '');
                              }}
                              className="form-input"
                            />

                            {values.searchMobile?.trim() && values.searchMobile.length > 5 && (
                              <>
                                {isSearchingMobile && <LoadingSpinner />}
                                {!isSearchingMobile && contactResultsMobile.length > 0 && (
                                  <div className="contact-results">
                                    {contactResultsMobile.map((item) => (
                                      <ContactCard
                                        key={`${item.contactid}-${item.firstname}`}
                                        contact={item}
                                        onSelect={() => {
                                          setFieldValue('searchContactId', item.contactid);
                                          setFieldValue('addContact', true);
                                          setContactResultsMobile([]);
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                                {!isSearchingMobile && contactResultsMobile?.length === 0 && (
                                  <NoResultsCard />
                                )}
                              </>
                            )}

                            <input
                              type="email"
                              placeholder="Email"
                              value={values.searchEmail}
                              onChange={(e) => {
                                setFieldValue('searchEmail', e.target.value);
                                searchContacts(e.target.value, e.target.value, 'email');
                              }}
                              onFocus={() => {
                                setContactResultsMobile([]);
                                setContactResultsFirstLastName([]);
                                setFieldValue('searchMobile', '');
                                setFieldValue('searchFirstName', '');
                                setFieldValue('searchLastName', '');
                              }}
                              className="form-input"
                            />

                            {values.searchEmail && values.searchEmail.length > 5 && (
                              <>
                                {isSearchingEmail && <LoadingSpinner />}
                                {!isSearchingEmail && contactResultsEmail.length > 0 && (
                                  <div className="contact-results">
                                    {contactResultsEmail.map((item) => (
                                      <ContactCard
                                        key={`${item.contactid}-${item.firstname}`}
                                        contact={item}
                                        onSelect={() => {
                                          setFieldValue('searchContactId', item.contactid);
                                          setFieldValue('addContact', true);
                                          setContactResultsMobile([]);
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                                {!isSearchingEmail && contactResultsEmail?.length === 0 && (
                                  <NoResultsCard />
                                )}
                              </>
                            )}
                          </div>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <div className="selected-contact-card">
                      <div className="contact-header">
                        <h3>👤 {selectedContact.firstname} {selectedContact.lastname}</h3>
                      </div>
                      <div className="contact-details">
                        {selectedContact.mobile && (
                          <p>📱 {selectedContact.mobile}</p>
                        )}
                        {selectedContact.email && (
                          <p>
                            📧 {selectedContact.email}
                            {selectedContact.emailVerified === 1 && <span className="verified">✓</span>}
                          </p>
                        )}
                        {selectedContact.agentfirstname && selectedContact.agentlastname && (
                          <p>💼 {selectedContact.agentfirstname} {selectedContact.agentlastname}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setSelectedContact(null);
                          setFieldValue('searchContactId', '');
                          setFieldValue('addContact', false);
                        }}
                      >
                        Change Contact
                      </button>
                    </div>
                  )}

                  <div className="switch-row">
                    <input
                      type="checkbox"
                      id="isAddContact"
                      checked={values.isAddContact === 1}
                      onChange={() => {
                        const newValue = values.isAddContact === 1 ? 0 : 1;
                        setFieldValue('isAddContact', newValue);
                        if (newValue === 1) {
                          setFieldValue('isSearchContact', 0);
                          setFieldValue('searchFirstName', '');
                          setFieldValue('searchLastName', '');
                          setFieldValue('searchMobile', '');
                          setFieldValue('searchEmail', '');
                          setSelectedContact(null);
                          setFieldValue('searchContactId', '');
                          setFieldValue('addContact', false);
                        }
                      }}
                    />
                    <label htmlFor="isAddContact">Add A New Contact</label>
                  </div>

                  {values.isAddContact && (
                    <div className="form-section">
                      <div className="input-group">
                        <input
                          type="text"
                          placeholder="First Name"
                          value={values.firstNameContact}
                          onChange={handleChange('firstNameContact')}
                          onBlur={handleBlur('firstNameContact')}
                          className="form-input"
                        />
                        <input
                          type="text"
                          placeholder="Last Name"
                          value={values.lastNameContact}
                          onChange={handleChange('lastNameContact')}
                          onBlur={handleBlur('lastNameContact')}
                          className="form-input"
                        />
                      </div>

                      <input
                        type="email"
                        placeholder="Email"
                        value={values.contactEmail}
                        onChange={(e) => {
                          setFieldValue('contactEmail', e.target.value);
                          setFieldValue('enabledForBroadCast', e.target.value?.trim() !== '' ? 1 : 0);
                        }}
                        onBlur={handleBlur('contactEmail')}
                        className="form-input"
                      />

                      <input
                        type="tel"
                        placeholder="Phone"
                        value={values.contactNumber}
                        onChange={(e) => {
                          setFieldValue('contactNumber', e.target.value);
                          setFieldValue('enableSMSBroadcast', e.target.value?.trim() !== '' ? 1 : 0);
                        }}
                        onBlur={handleBlur('contactNumber')}
                        className="form-input"
                      />

                      <h3 className="section-title">Contact Type</h3>
                      <div className="contact-types-grid">
                        {contactTypes.map((type) => (
                          <div key={type.ContactTypeID} className="switch-row">
                            <label>{type.Type}</label>
                            <input
                              type="checkbox"
                              checked={values.contactTypeID.split(',').includes(type.ContactTypeID.toString())}
                              onChange={() => {
                                let currentSelection = values.contactTypeID 
                                  ? values.contactTypeID.split(',').filter(id => id?.trim() !== '') 
                                  : [];

                                if (type.ContactTypeID.toString() === '6') {
                                  if (currentSelection.includes('6')) {
                                    currentSelection = currentSelection.filter(id => id !== '6');
                                  } else {
                                    currentSelection = ['6'];
                                  }
                                } else {
                                  if (currentSelection.includes('6')) {
                                    currentSelection = currentSelection.filter(id => id !== '6');
                                  }

                                  if (currentSelection.includes(type.ContactTypeID.toString())) {
                                    currentSelection = currentSelection.filter(id => id !== type.ContactTypeID.toString());
                                  } else {
                                    currentSelection.push(type.ContactTypeID.toString());
                                  }
                                }

                                setFieldValue('contactTypeID', currentSelection.join(','));
                              }}
                              disabled={values.contactTypeID.includes('6') && type.ContactTypeID.toString() !== '6'}
                            />
                          </div>
                        ))}
                      </div>

                      <h3 className="section-title">Enquiry Source</h3>
                      <select
                        value={values.contactSourceId}
                        onChange={(e) => setFieldValue('contactSourceId', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select Enquiry Source</option>
                        {enquirySource.map((source) => (
                          <option key={source.contactSourceId} value={source.contactSourceId}>
                            {source.source}
                          </option>
                        ))}
                      </select>

                      <h3 className="section-title">Salesperson</h3>
                      <select
                        value={values.salespersonid}
                        onChange={(e) => setFieldValue('salespersonid', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select Salesperson</option>
                        {Salesperson.map((person) => (
                          <option key={person.userid} value={person.userid}>
                            {person.salespersonName}
                          </option>
                        ))}
                      </select>

                      <h3 className="section-title">Broadcast Options</h3>
                      <div className="broadcast-options">
                        <div className="switch-row">
                          <label>SMS Broadcast</label>
                          <input
                            type="checkbox"
                            checked={values.enableSMSBroadcast === 1}
                            onChange={(e) => setFieldValue('enableSMSBroadcast', e.target.checked ? 1 : 0)}
                          />
                        </div>
                        <div className="switch-row">
                          <label>Email Broadcast</label>
                          <input
                            type="checkbox"
                            checked={values.enabledForBroadCast === 1}
                            onChange={(e) => setFieldValue('enabledForBroadCast', e.target.checked ? 1 : 0)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <hr className="divider" />
              </>
            )}

            {feedbackType === 2 && (
              <>
                <h2 className="section-title">Interest & Contract</h2>
                
                <select
                  value={values.interestLevel}
                  onChange={(e) => {
                    setFieldValue('interestLevel', e.target.value);
                    setFieldValue('interested', 1);
                  }}
                  className="form-select"
                >
                  <option value="">Select interest level</option>
                  {interestLevels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>

                <div className="checkbox-row">
                  <input
                    type="checkbox"
                    id="contractRequested"
                    checked={values.contractRequested === 1}
                    onChange={() => setFieldValue('contractRequested', values.contractRequested === 1 ? 0 : 1)}
                  />
                  <label htmlFor="contractRequested">Requested Contract</label>
                </div>

                <hr className="divider" />
              </>
            )}

            {feedbackType === 3 && (
              <div>
                <div className="checkbox-row">
                  <input
                    type="checkbox"
                    id="interested"
                    checked={values.interested === 1}
                    onChange={() => setFieldValue('interested', values.interested === 1 ? 0 : 1)}
                  />
                  <label htmlFor="interested">Interested</label>
                </div>
                <hr className="divider" />
              </div>
            )}

            <h2 className="section-title">Date</h2>
            {values.useDatePicker === 0 && feedbackType === 2 && (
              <select
                value={values.propertyOpenTimeId}
                onChange={(e) => {
                  setFieldValue('propertyOpenTimeId', e.target.value);
                  setFieldValue('addDate', getCurrentDateInISOFormat(new Date()));
                }}
                className="form-select"
              >
                <option value="">Select Opentime</option>
                {Openhomes.map((opentime) => (
                  <option key={opentime.propertyopentimeid} value={opentime.propertyopentimeid}>
                    {opentime.opentimeLabel}
                  </option>
                ))}
              </select>
            )}

            {feedbackType === 2 && (
              <div className="switch-row">
                <input
                  type="checkbox"
                  id="useDatePicker"
                  checked={values.useDatePicker === 1}
                  onChange={(e) => {
                    setFieldValue('useDatePicker', e.target.checked ? 1 : 0);
                    setFieldValue('propertyOpenTimeId', '');
                    setFieldValue('addDate', '');
                  }}
                />
                <label htmlFor="useDatePicker">Use Date Picker</label>
              </div>
            )}

            {(values.useDatePicker === 1 || feedbackType === 1 || feedbackType === 3) && (
              <input
                type="date"
                value={values.addDate}
                onChange={(e) => setFieldValue('addDate', e.target.value)}
                className="form-input"
              />
            )}

            <hr className="divider" />

            {feedbackType === 2 && (
              <>
                <h2 className="section-title">Price Range</h2>
                <div className="price-container">
                  <input
                    type="number"
                    placeholder="From ($)"
                    value={values.priceFrom}
                    onChange={handleChange('priceFrom')}
                    onBlur={handleBlur('priceFrom')}
                    className="form-input"
                  />
                  <input
                    type="number"
                    placeholder="To ($)"
                    value={values.priceTo}
                    onChange={handleChange('priceTo')}
                    onBlur={handleBlur('priceTo')}
                    className="form-input"
                  />
                </div>
                <hr className="divider" />
              </>
            )}

            <h2 className="section-title">Comments</h2>
            <textarea
              placeholder="Comments"
              value={values.feedback}
              onChange={handleChange('feedback')}
              onBlur={handleBlur('feedback')}
              className="form-textarea"
              rows="4"
            />

            {feedbackType === 2 && (
              <>
                <textarea
                  placeholder="Agent Comments"
                  value={values.agentComments}
                  onChange={handleChange('agentComments')}
                  onBlur={handleBlur('agentComments')}
                  className="form-textarea"
                  rows="4"
                />
                <textarea
                  placeholder="Agent's Feedback to Client"
                  value={values.agentFeedback}
                  onChange={handleChange('agentFeedback')}
                  onBlur={handleBlur('agentFeedback')}
                  className="form-textarea"
                  rows="4"
                />
              </>
            )}

            <hr className="divider" />

            {(feedbackType === 2 || feedbackType === 3) && (
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="addToContactHistory"
                  checked={values.addToContactHistory === 1}
                  onChange={() => setFieldValue('addToContactHistory', values.addToContactHistory === 1 ? 0 : 1)}
                />
                <label htmlFor="addToContactHistory">Add Inspection Details to Seller's Contact History</label>
              </div>
            )}
          </div>

          <div className="form-footer">
           <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
        >
            {isSubmitting ? (
            <>
                <div className="spinner"></div>
                Saving...
            </>
            ) : (
            'Save Feedback'
            )}
        </button>
        </div>
    </div>
    )}
                </Formik>
            );
          };
          
          export default FeedbackAdd;